import type { SessionUser } from '@accounts/shared';
import type { ReactNode } from 'react';

import { STR } from '../../../i18n';
import { canEnter, type SurfaceId, surfaceById } from '../../../lib/surfaces';
import { SectionMenu, type SectionMenuEntry } from '../../../ui/SectionMenu';
import { AdminShell } from '../AdminShell';

import { CatalogPanel } from './CatalogPanel';
import { ItEmailsPanel } from './itEmail/ItEmailsPanel';
import { MailPanel } from './mail/MailPanel';
import { useWorkbookExport } from './matrix/useWorkbookExport';
import { MatrixPanel } from './MatrixPanel';
import { OverviewPanel } from './OverviewPanel';
import { QueuePanel } from './QueuePanel';
import { useAccessCounts } from './useAccessCounts';

/**
 * THE ACCESS HUB (SIR-7424, FINAL demo R3-2): one rail entry, a left sub-nav of its
 * sections (the shared `SectionMenu`), the section on the right. Every section is its OWN
 * surface in the one table, so
 * the sub-nav is derived exactly as the rail is: an entry renders IFF `canEnter` passes for
 * that section's declared gate. HR and IT see Overview, Queue, IT emails and Emails; Catalog
 * and Matrix only where a Settings → Access switch is on for their role (SIR-7864), and a
 * direct address to either is refused by the route guard before this file renders.
 *
 * IT emails (spec row 15) is the demo's third section; HR enters it too, as both see every
 * IT email. Emails (the answer emails Accounts captures or sends, board 19 Oct) is the
 * reference section after the Matrix, view only for everybody.
 */
export type AccessSection = 'overview' | 'queue' | 'itEmails' | 'catalog' | 'matrix' | 'emails';

const SECTIONS: readonly { id: AccessSection; surface: SurfaceId; label: string }[] = [
  { id: 'overview', surface: 'access', label: STR.accessHub.overview },
  { id: 'queue', surface: 'accessQueue', label: STR.accessHub.queue },
  { id: 'itEmails', surface: 'accessItEmails', label: STR.accessHub.itEmails },
  { id: 'catalog', surface: 'accessCatalog', label: STR.accessHub.catalog },
  { id: 'matrix', surface: 'accessMatrix', label: STR.accessHub.matrix },
  { id: 'emails', surface: 'accessEmails', label: STR.accessHub.emails },
];

/** The demo draws a rule between the working sections and the admin's reference ones. */
const RULE_BEFORE: AccessSection = 'catalog';

export function AccessHub({ section, user }: { section: AccessSection; user: SessionUser }) {
  const current = SECTIONS.find((entry) => entry.id === section);
  const surfaceId: SurfaceId = current?.surface ?? 'access';
  const visible = SECTIONS.filter((entry) => canEnter(user, surfaceById(entry.surface).capability));

  // The same counts the rail badge shows, from the same read the Queue uses.
  const counts = useAccessCounts(user);
  const waiting = counts.ready ? counts.waiting : null;
  const anyOverdue = counts.anyOverdue;
  const requests = counts.waitingRequests;
  const workbookExport = useWorkbookExport();

  let body: ReactNode = null;
  switch (section) {
    case 'overview':
      body = <OverviewPanel user={user} />;
      break;
    case 'queue':
      body = <QueuePanel user={user} />;
      break;
    case 'itEmails':
      body = <ItEmailsPanel user={user} />;
      break;
    case 'catalog':
      // "See Catalog" is view only: Catalog editing stays the administrator's (SIR-7864).
      body = <CatalogPanel readOnly={!user.isAdmin} />;
      break;
    case 'matrix':
      body = <MatrixPanel user={user} />;
      break;
    case 'emails':
      body = <MailPanel />;
      break;
  }

  const entries: SectionMenuEntry[] = visible.map((entry) => ({
    id: entry.id,
    path: surfaceById(entry.surface).path,
    label: entry.label,
    ruleBefore: entry.id === RULE_BEFORE,
    trailing:
      entry.id === 'queue' ? (
        <>
          {requests > 0 ? (
            // The rail's red count, on the Queue entry too (spec row 14): the same
            // number from the same read, with the words for a screen reader.
            <span
              className="c-red"
              title={STR.accessHub.requestsWaiting(requests)}
              aria-label={STR.accessHub.requestsWaiting(requests)}
              style={{
                borderWidth: 1,
                borderStyle: 'solid',
                minWidth: 16,
                height: 16,
                padding: '0 4px',
                borderRadius: 999,
                boxSizing: 'border-box',
                display: 'grid',
                placeItems: 'center',
                fontSize: 9.5,
                fontWeight: 700,
                lineHeight: 1,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {requests > 99 ? '99+' : requests}
            </span>
          ) : null}
          {waiting !== null ? (
            <span
              className={anyOverdue ? 'tx-danger-strong' : 'tx-subtle'}
              title={anyOverdue ? STR.accessHub.overdueSome(waiting) : STR.accessHub.waiting(waiting)}
              style={{
                marginLeft: 'auto',
                fontSize: 11.5,
                fontWeight: 650,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {waiting}
            </span>
          ) : null}
        </>
      ) : null,
  }));

  return (
    <AdminShell
      surfaceId={surfaceId}
      // The server resolves `exportMatrix` (true for admin; for HR or IT only with See Matrix).
      primary={section === 'matrix' && user.access.exportMatrix ? workbookExport : undefined}
    >
      <SectionMenu label={STR.accessHub.sections} entries={entries} current={section}>
        {body}
      </SectionMenu>
    </AdminShell>
  );
}
